import {ImportingDataTypes} from "../../types";
import {joinDelimitedList, parseDelimitedList} from "../transformers/shared/inventory-availability-validation";
import {createInvalidPartSection, createInvalidWarehouseSection, PreventativeAutocompleteType, PreventionInvalidityCode,} from "./shared";

/**
 * @param {*} val
 * @param {string} [splitter]
 * @returns {string[]}
 */
const formatSerialsForRow = (val, splitter = ',') => parseDelimitedList(val, splitter);

/**
 * Prefills the preventative text input from a formatted serial list or delimited string.
 * @param {*} val
 * @returns {string}
 */
const extractSerialNumbersValue = (val) => {
    if (Array.isArray(val)) {
        return joinDelimitedList(val, ', ');
    }
    if (val == null) {
        return '';
    }
    return String(val);
};

export const preventativeInteraction = {
    datasourceId: ImportingDataTypes.setInventoryAvailability,
    sections: [
        createInvalidPartSection('partNo'),
        createInvalidWarehouseSection('warehouseCode'),
        {
            sectionKey: 'missingSetInventoryAvailabilityFields',
            sectionTitle: 'Missing Required Fields',
            invalidityCode: PreventionInvalidityCode.MISSING_REQUIRED_FIELDS,
            fields: [
                {
                    key: 'warehouseCode',
                    label: 'Warehouse',
                    type: PreventativeAutocompleteType.WAREHOUSE,
                    extractValue: (o) => (typeof o === 'string' ? o : o?.code ?? null),
                    modifyRow: (row, val) => ({...row, warehouseCode: val}),
                    autocompleteProps: {
                        placeholder: 'Search for warehouse...',
                        getOptionLabel: (o) => (o?.name ? `${o.name} (${o.code})` : o?.code ?? ''),
                        isOptionEqualToValue: (o, v) => o?.code === v?.code,
                    },
                },
                {
                    key: 'partNo',
                    label: 'Part No',
                    type: PreventativeAutocompleteType.PART,
                    extractValue: (o) => (typeof o === 'string' ? o : o?.partNo ?? null),
                    modifyRow: (row, val) => ({...row, partNo: val}),
                    autocompleteProps: {
                        placeholder: 'Search for part...',
                        getOptionLabel: (o) => o?.partNo ?? '',
                        isOptionEqualToValue: (o, v) => o?.partNo === v?.partNo,
                    },
                },
                {
                    key: 'quantity',
                    label: 'Quantity',
                    type: 'input',
                    textFieldProps: {inputProps: {type: 'number', min: 0}},
                    modifyRow: (row, val) => ({...row, quantity: val}),
                },
                {
                    key: 'avgCost',
                    label: 'Avg Cost',
                    type: 'input',
                    textFieldProps: {inputProps: {type: 'number', min: 0, step: 'any'}},
                    modifyRow: (row, val) => ({...row, avgCost: val}),
                },
            ],
        },
        {
            sectionKey: 'invalidQuantityOrAvgCost',
            sectionTitle: 'Invalid Quantity or Avg Cost',
            invalidityCode: PreventionInvalidityCode.INVALID_AMOUNT,
            fields: [
                {
                    key: 'quantity',
                    label: 'Quantity',
                    type: 'input',
                    textFieldProps: {inputProps: {type: 'number', min: 0}},
                    modifyRow: (row, val) => ({...row, quantity: val}),
                },
                {
                    key: 'avgCost',
                    label: 'Avg Cost',
                    type: 'input',
                    textFieldProps: {inputProps: {type: 'number', min: 0, step: 'any'}},
                    modifyRow: (row, val) => ({...row, avgCost: val}),
                },
            ],
        },
        {
            sectionKey: 'serialCountMismatch',
            sectionTitle: 'Serial Count Does Not Match Quantity',
            invalidityCode: PreventionInvalidityCode.SERIAL_COUNT_MISMATCH,
            fields: [
                {
                    key: 'quantity',
                    label: 'Quantity',
                    type: 'input',
                    textFieldProps: {inputProps: {type: 'number', min: 0}},
                    modifyRow: (row, val) => ({...row, quantity: val}),
                },
                {
                    key: 'serialNumbers',
                    label: 'Serial Numbers (delimiter-separated, distinct)',
                    type: 'input',
                    extractValue: extractSerialNumbersValue,
                    modifyRow: (row, val) => ({
                        ...row,
                        serialNumbers: formatSerialsForRow(val),
                    }),
                },
            ],
        },
    ],
};
